import type { ReactElement } from 'react';
import type {
  DevicePosture,
  DeviceUpdateStateCounts,
} from '@kirin/api-client/contracts/device-inventory';
import type { AgentUpdateState } from '@kirin/api-client/contracts/agent-auto-update';
import { AGENT_UPDATE_STATE_LABELS } from '@/components/shared/agentUpdateStateLabels';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type Tone = 'neutral' | 'good' | 'warn' | 'info' | 'bad';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'text-foreground',
  good: 'text-emerald-700 dark:text-emerald-400',
  warn: 'text-amber-600 dark:text-amber-400',
  info: 'text-blue-600 dark:text-blue-400',
  bad: 'text-red-600 dark:text-red-400',
};

/** Mockup 1 tile: the label above a large coloured number. */
function Tile({
  label,
  value,
  tone,
  title,
}: {
  label: string;
  value: number;
  tone: Tone;
  title?: string;
}): ReactElement {
  return (
    <div
      data-testid="device-posture-tile"
      title={title}
      className="flex min-w-[9rem] flex-1 flex-col gap-1 rounded-xl border bg-card px-4 py-3"
    >
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className={cn('text-3xl font-bold leading-none', TONE_CLASS[tone])}>
        {value}
      </span>
    </div>
  );
}

/** Update states other than "Up to date", in the order the tiles list them. */
const OTHER_UPDATE_STATES: (AgentUpdateState | 'unknown')[] = [
  'updating',
  'pending_reboot',
  'needs_attention',
  'update_failed',
  'pinned_by_mdm',
  'not_available',
  'off',
  'unknown',
];

function updateStateLabel(state: keyof DeviceUpdateStateCounts): string {
  return state === 'unknown'
    ? 'No update status'
    : AGENT_UPDATE_STATE_LABELS[state];
}

/**
 * Mockup 1 posture summary: total endpoints, agent up to date, update
 * available, shared machines, open alerts (last 7 days). With automatic updates on, a line under it counts the
 * endpoints in every other update state.
 */
export function DevicePostureTiles({
  posture,
  isLoading,
  isError,
  showUpdateStates,
}: {
  posture: DevicePosture | undefined;
  isLoading: boolean;
  isError: boolean;
  showUpdateStates: boolean;
}): ReactElement | null {
  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-3" aria-label="Loading posture">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-[84px] min-w-[9rem] flex-1" />
        ))}
      </div>
    );
  }
  if (isError || !posture) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Couldn&apos;t load the endpoint summary. Please try again.
      </p>
    );
  }

  const otherStates = OTHER_UPDATE_STATES.filter(
    state => posture.updateStateCounts[state] > 0
  );

  return (
    <section className="flex flex-col gap-2" aria-label="Endpoint posture">
      <div className="flex flex-wrap gap-3">
        <Tile
          label="Total endpoints"
          value={posture.totalDevices}
          tone="neutral"
          title={`${posture.singleUserDevices} single user`}
        />
        <Tile
          label="Agent up to date"
          value={posture.upToDateDevices}
          tone="good"
          title={
            posture.latestAgentVersion
              ? `Latest v${posture.latestAgentVersion}`
              : undefined
          }
        />
        <Tile
          label="Update available"
          value={posture.updateAvailableDevices}
          tone="warn"
        />
        <Tile
          label="Shared machines"
          value={posture.sharedDevices}
          tone="info"
        />
        <Tile
          label="Open alerts"
          value={posture.openAlertCount}
          tone="bad"
          title={`Last 7 days, on ${posture.devicesWithAlerts} endpoint${posture.devicesWithAlerts === 1 ? '' : 's'}`}
        />
      </div>
      {showUpdateStates && otherStates.length > 0 && (
        <div
          data-testid="device-update-state-counts"
          className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
        >
          <span>Automatic updates:</span>
          {otherStates.map(state => (
            <span key={state}>
              {updateStateLabel(state)}{' '}
              <span className="font-medium text-foreground">
                {posture.updateStateCounts[state]}
              </span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
